/* eslint-disable @typescript-eslint/naming-convention */
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { Get, Post, Put } from 'config/apiServiceConfig'
import { buildQueryParams } from 'utils/helpers/queryParams'

export type StatusValueEnum =
  | 'CREATED'
  | 'NEW'
  | 'PICKING'
  | 'PACKING'
  | 'STAGING'
  | 'LOADING'
  | 'READY TO SHIP'
  | 'ON DELIVERY'
  | 'DELIVERED'
  | 'IN PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'

export type GetSupplyOrderListParamsType = Partial<{
  'pagination.page_index': number
  'pagination.page_size': number
  status: StatusValueEnum
  supply_order_number: string
  route: number
  origin_ids: string
  destination_ids: string
  remarks: string
  supply_order_date: string
  picker_id: number
  checker_staff_id: number
  packer_staff_id: number
  ranking_from: number
  ranking_to: number
  picking_area_id: number
  created: boolean
  route_wave_id: number
}>

export type GetSupplyOrderListResponseType = {
  error: {
    status: boolean
    code: number
    message: string
  }
  pagination: {
    page_index: number
    sort: string
    page_size: number
    direction: string
  }
  data: {
    total_qty: number
    already_set_checker_packer: boolean
    id: number
    supply_order_date: string
    total_rack: number
    origin_id: number
    already_loading: boolean
    supply_order_type: string
    origin_name: string
    supply_order_loading_id: number
    picking_area: string
    supply_order_number: string
    total_item: number
    ranking: number
    picking_area_id: number
    created_at: string
    destination_id: number
    is_grouped: boolean
    group_parent_id?: string
    created_by: string
    count_so_in_group: number
    destination_name: string
    status: StatusValueEnum
    remarks: string
    is_picking_completed: boolean
    route_wave_name: string
    attribute: {
      is_xdock: boolean
    }
  }[]
}

export const getSupplyOrderListAPI = (params: GetSupplyOrderListParamsType) =>
  axiosInstanceApiGateway.Get<GetSupplyOrderListResponseType, true>({
    url: `/wims/internal/v1/supply-orders`,
    params,
    config: {
      paramsSerializer: {
        serialize: (p) => buildQueryParams(p).toString(),
      },
    },
  })

// --------------------------------

export type GetSupplyOrderRemarksResponseType = {
  error: {
    status: boolean
    code: number
    message: string
  }
  data: {
    id: number
    code: string
    description: string
  }[]
}

export const getSupplyOrderRemarksAPI = () =>
  Get<GetSupplyOrderRemarksResponseType>({
    url: '/api/wms/v2/supply-order/remarks',
  })

// --------------------------------

export type GetSupplyOrderGrandDataResponseType = {
  data: {
    done_picking_quantity: number
    done_picking_racks: number
    done_picking_sku: number
    picking_activity: {
      activity: string
      duration: string
      endAt: string
      name: string
      startAt: string
      status: string
      supplyOrderItemId: number
    }[]
    singleKoli: boolean
    supply_order_created_at: number
    supply_order_created_by: string
    supply_order_date: string
    supply_order_destination: string
    supply_order_id: number
    supply_order_items: {
      product_name: string
    }[]
    supply_order_number: string
    supply_order_origin: string
    supply_order_origin_id: number
    supply_order_status: string
    total_quantity: number
    total_rack: number
    total_sku: number
  }
}

export const getSupplyOrderGrandDataAPI = (
  supplyOrderId: number,
  params?: {
    staffIdList: number
  },
) =>
  Get<GetSupplyOrderGrandDataResponseType>({
    url: `/api/supply-order-grand/${supplyOrderId}/data`,
    params,
  })

export const postSupplyOrderAssignPickerAPI = (
  supplyOrderId: number,
  payload: {
    pickerId: number
  },
) =>
  Post<{
    data: {
      message: string
    }
  }>({
    url: `/api/wms/v2/supply-order/${supplyOrderId}/picking/assign`,
    data: payload,
  })

export const postSupplyOrderBulkAssignPickerAPI = (payload: {
  picker_id: number
  supply_order_ids: number[]
}) =>
  axiosInstanceApiGateway.Post<{ message: string }, true>({
    url: `/wims/internal/v1/supply-orders/pickers`,
    data: payload,
  })

// --------------------------------

export const postSupplyOrderCopyAPI = (supplyOrderId: number) =>
  Post({
    url: `/api/supply-order-grand/${supplyOrderId}/copy`,
  })

export const putSupplyOrderStatusAPI = (
  supplyOrderId: number,
  payload: {
    status: string
  },
) =>
  Put({
    url: `/api/wms/v2/supply-order/${supplyOrderId}/status`,
    data: payload,
  })

// --------------------------------

type GetStaffManagementRequestType = {
  role: string
  locationId: number
  params?: {
    locationType?: string
  }
}

export type GetStaffManagementResponseType = {
  error: {
    status: boolean
    code: number
    message: string
  }
  data: {
    id: number
    fullName: string
    trackerID: string
  }[]
}

export const getStaffManagementAPI = ({
  locationId,
  role,
  params,
}: GetStaffManagementRequestType) =>
  Get<GetStaffManagementResponseType>({
    url: `/api/staff-management/staff/${locationId}/${role}`,
    params,
  })

// --------------------------------

export type GetSupplyOrderPackingDetailResponseType = {
  supply_order_id: number
  supply_order_number: string
  packers: {
    id: number
    name: string
  }[]
  checker: {
    id: number
    name: string
  }
  supply_order_items: {
    product_sku: string
    product_name: string
    product_uom: string
    request_quantity: number
    total_actual_quantity: number
    supply_order_item_details: {
      supply_order_item_detail_id: number
      actual_quantity: number
      status_code: string
      koli_number: string
      status_name: string
      expiry_date: number
      manual_expiry_date: number
    }[]
    substitute_item: boolean
    product_type: string
    supply_order_item_id: number
    status_id: number
    status_note_id: number
    product_is_qr: boolean
    status: string
    product_id: number
  }[]
}

export const getSupplyOrderPackingDetailAPI = (supplyOrderId: number) =>
  axiosInstanceApiGateway.Get<GetSupplyOrderPackingDetailResponseType>({
    url: `/wms/internal/v1/supply-order/${supplyOrderId}/packing`,
  })

// --------------------------------

type GetSupplyOrderGrandKoliLabelResponseType = {
  data: {
    supplyOrderNumber: string
    koliCode: string
    koliNumber: string
    origin: string
    destination: string
    skuDetails: {
      skuNumber: string
      quantity: number
    }[]
  }[]
  error: {
    code: number
    message: string
    status: boolean
  }
}

export const getSupplyOrderGrandKoliLabelAPI = (supplyOrderId: number) =>
  Get<GetSupplyOrderGrandKoliLabelResponseType>({
    url: `/api/v2/supply-order-grand/${supplyOrderId}/koli-label`,
  })

// --------------------------------

type GetSupplyOrderDeliveryDocumentResponseType = {
  data: {
    locationName: string
    detail: {
      soNumber: string
      soStatus: string
      totalKoli: number
    }[]
  }[]
  error: {
    status: boolean
    code: number
    message: string
  }
}

export const getSupplyOrderDeliveryDocumentAPI = (params: { id: string }) =>
  Get<GetSupplyOrderDeliveryDocumentResponseType>({
    url: '/api/supply-order-grand/delivery-document',
    params,
  })

export type GetSupplyOrderDeliveryDocumentLoadingResponseType = {
  data: {
    driverName: string
    driverType: string
    freshFinalTemperature: number
    freshInitialTemperature: number
    originLocationName: string
    plateNumber: string
    travelDocumentNumber: string
    supplyOrderData: {
      supplyOrderNumber: string
      koliDispatched: number
      koliSent: number
    }[]
    groupDeliveryDocuments: {
      destinationLocationName: string
      supplyOrderData: {
        supplyOrderNumber: string
        koliDispatched: number
        koliSent: number
      }[]
    }
  }
  error: {
    status: boolean
    code: number
    message: string
  }
}

export const getSupplyOrderDeliveryDocumentLoadingAPI = (params: { loadingIDs: string }) =>
  Get<GetSupplyOrderDeliveryDocumentLoadingResponseType>({
    url: '/api/wms/v2/supply-order/print-delivery-document',
    params,
  })

export const getSupplyOrderDeliveryDocumentLoadingDetailAPI = (id: number) =>
  Get<GetSupplyOrderDeliveryDocumentLoadingResponseType>({
    url: `/api/wms/v2/supply-order/print-delivery-document/${id}`,
  })

export const postNewGroupSupplyOrderAPI = (payload: { supplyOrderIds: number[] }) =>
  Post<{
    message: string
  }>({ url: '/api/wms/v1/supply-order/loading/grouping-so', data: payload })

export const putCancelGroupSupplyOrderAPI = (soGroupId: string) =>
  Put<{ message: string }>({ url: `/api/wms/v1/supply-order/loading/${soGroupId}/cancel` })

export const putStartSoLoadingActivityAPI = (data: { loading_id: number }) =>
  axiosInstanceApiGateway.Put<
    {
      message: string
    },
    true
  >({ url: '/wms/internal/v1/supply-order/loading/start-activity', data })

export type GetPrintSupplyOrderGroupResponseType = {
  data: {
    fileUrl: string
  }
  message: string
}

export const getPrintSupplyOrderGroupAPI = (soGroupId: string) =>
  Get<GetPrintSupplyOrderGroupResponseType>({
    url: `/api/wms/v1/supply-order/loading/${soGroupId}/file`,
  })

// --------------------------------

type StatusEnumType =
  | 'CREATED'
  | 'NEW'
  | 'PICKING'
  | 'PACKING'
  | 'STAGING'
  | 'LOADING'
  | 'READY TO SHIP'
  | 'ON DELIVERY'
  | 'DELIVERED'
  | 'IN PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'

export type GetSupplyOrderDetailResponseType = {
  data: {
    supplyOrderId: number
    supplyOrderCreatedAt: number
    supplyOrderNumber: string
    locationOriginId: number
    locationOriginName: string
    locationDestinationId: number
    locationDestinationName: string
    supplyOrderCreatedBy: string
    supplyOrderType: string
    supplyOrderDate: number
    supplyOrderStatus: StatusEnumType
    supplyOrderReff: string | null
    supplyOrderRequestBy: string
    supplyOrderRemarks: string | null
    supplyOrderShippingInstructions: string | null
    supplyOrderActivities: {
      activityName: string
      startDate: string
      endDate: string | null
      duration: string
      user: string
      userLogList: string[]
      activityLogList: {
        picking?: {
          name: string
          activity: string
          supplyOrderItemId: number | null
          status: StatusEnumType
          startAt: string
          endAt: string | null
          duration: string
        }[]
        packing?: {
          name: string
          activity: string
          supplyOrderItemId: number | null
          status: StatusEnumType
          startAt: string
          endAt: string | null
          duration: string
        }[]
        checking?: {
          name: string
          activity: string
          supplyOrderItemId: number | null
          status: StatusEnumType
          startAt: string
          endAt: string | null
          duration: string
        }[]
        putaway?: {
          name: string
          activity: string
          supplyOrderItemId: number | null
          status: StatusEnumType
          startAt: string
          endAt: string | null
          duration: string
        }[]
        loading?: {
          name: string
          activity: string
          supplyOrderItemId: number | null
          status: StatusEnumType
          startAt: string
          endAt: string | null
          duration: string
        }[]
      }
    }[]
    supplyOrderProductSummary: {
      totalSku: number
      netWeight: number
      volumeWeight: number
      totalQty: number
      grossWeight: number
      volume: number
    }
  }
}

export const getSupplyOrderDetailAPI = (supplyOrderId: number) =>
  Get<GetSupplyOrderDetailResponseType>({
    url: `/api/wms/v2/supply-order/${supplyOrderId}`,
  })

export type PutSupplyOrderDetailRequestType = {
  supplyOrderItemRequest?: {
    productDetailId?: number
    productId: number
    productQrCode: string
    productRequestQuantity: number
    productStatusId: number
    productStatusNoteId: number | null
    productStock: number
    supplyOrderItemId?: number
    supplyOrderItemIdList?: number[]
  }[]
  supplyOrderRequest: {
    createdBy?: string
    locationDestinationId: number
    locationOriginId: number
    remarks: string
    poRef?:
      | {
          destinationId: number
          destinationName: string
          poNumber: string
          vendorId: number
          vendorName: string
        }
      | object
    requestBy: string
    shippingInstructions: string
    soDate: number
    soReff: string | null
    soType: string
    status?: string
  }
}

export type PutSupplyOrderDetailResponseType = {
  data: {
    supplyOrderId: number
  }
}

export const putSupplyOrderDetailAPI = (
  supplyOrderId: number,
  payload: PutSupplyOrderDetailRequestType,
) =>
  Put<PutSupplyOrderDetailResponseType>({
    url: `/api/wms/v2/supply-order/${supplyOrderId}`,
    data: payload,
  })

export type PostSupplyOrderDetailRequestType = {
  locationDestinationId: number
  locationOriginId: number
  remarks: string
  poRef?:
    | {
        destinationId: number
        destinationName: string
        poNumber: string
        vendorId: number
        vendorName: string
      }
    | object
  requestBy: string
  shippingInstructions: string
  soDate: number
  soReff: string | null
  soType: string
  supplyOrderItems: {
    productId: number
    productQrCode: string
    productRequestQuantity: number
    productStatusId: number
    productStatusNoteId: number | null
    productStock: number
  }[]
}

export type PostSupplyOrderDetailResponseType = {
  data: {
    supplyOrderId: number
  }
}

export const postSupplyOrderDetailAPI = (payload: PostSupplyOrderDetailRequestType) =>
  Post<PostSupplyOrderDetailResponseType>({
    url: `/api/wms/v2/supply-order`,
    data: payload,
  })

export type GetSupplyOrderDetailItemResponseType = {
  data: {
    content: {
      supplyOrderProductDetailId: number
      supplyOrderItemId: number
      supplyOrderItemIdList: number[]
      productId: number
      productSku: string
      productName: string
      productImage: string
      productExpDate: string
      productRequestQuantity: number
      productUom: string
      productDimension: string
      productDimensionUom: string
      productVolume: number
      productVolumeUom: string
      productQrCode: string
      productInventoryStatusId: number
      productInventoryStatusNoteId: number | null
      productStock: number
      weight: string
    }[]
    numberOfElements: number
    pageable: {
      pageNumber: number
      pageSize: number
    }
  }
}

export const getSupplyOrderDetailItemAPI = (
  supplyOrderId: number,
  params: Partial<{
    pageIndex: number
    pageSize: number
    name: string
  }>,
) =>
  Get<GetSupplyOrderDetailItemResponseType>({
    url: `/api/wms/v2/supply-order/${supplyOrderId}/item`,
    params,
  })

export const getSupplyOrderTypeAPI = () =>
  Get<{ data: string[] }>({
    url: '/api/wms/v2/supply-order/type',
  })

export type BusinessTaggingValueType = 'SCHEDULE_DELIVERY' | 'ASTRO_GO' | 'QUICK_COMMERCE'

export type GetSOBusinessTaggingConfigResponseType = {
  data: {
    business_taggings: BusinessTaggingValueType[]
    hide_business_tagging_null: boolean
  }
}

export const getSOBusinessTaggingConfigAPI = (destinationId: number) =>
  axiosInstanceApiGateway.Get<GetSOBusinessTaggingConfigResponseType, true>({
    url: `/wms/internal/v1/supply-order/business-tagging/config/${destinationId}`,
  })

export type GetSOProductsDirectRequestType = Partial<{
  pageSize: number
  pageIndex: number
  productName: string
  locationId: number
  statusId: number
  statusNoteId: number
  hideBusinessTaggingNull: boolean
  businessTaggings: string
  productTypeIds: string
}>

export type GetSOProductsDirectResponseType = {
  data: {
    businessTagging: BusinessTaggingValueType[]
    businessTaggingLabel: string
    businessTaggings: {
      id: number
      key: BusinessTaggingValueType
      type: string
      value: string
    }[]
    productId: number
    productImage: string
    productName: string
    productSku: string
    productExpiryDate?: number
    productStock: number
    productInventoryStatus: string
    productInventoryStatusId: number
    productInventoryStatusNote: string
    productInventoryStatusNoteId: number
  }[]
}

export const getSOProductsDirectAPI = (params?: GetSOProductsDirectRequestType) =>
  Get<GetSOProductsDirectResponseType>({
    url: '/api/supply-order-grand/product-group',
    params,
  })

// FE-side filter/pagination state for the Outbound SO Add Product picker.
// - `purchaseOrderId` is forwarded to the new endpoint as `purchase_order_id`, only for return SOs.
// - `soType` is used by the thunk to decide whether to send `purchase_order_id`.
// - `pageIndex` is 0-based here (legacy UI); the thunk shifts it to the 1-based `pagination.page_index`.
export type GetSOProductsRequestType = Partial<{
  pageSize: number
  pageIndex: number
  purchaseOrderId: number
  soType: string
  name: string
}>

// UI row contract for the Outbound SO Add Product table. The thunk maps the new
// `/wims/internal/v1/inventory-details:groups` response back into this shape so the
// slice / Table / Row components stay unchanged.
export type GetSOProductsResponseType = {
  data: {
    content: {
      productDetailId: number
      productExpiryDate: string | null
      productId: number
      productImage: string | null
      productInventoryNote: string | null
      productInventoryNoteId: number | null
      productInventoryStatus: string
      productInventoryStatusId: number
      productName: string
      productQrCode: string
      productRackId: number | null
      productRackName: string | null
      productSku: string
      productStock: number
      productUniqueKey: string
    }[]
  }
}

export type GetInventoryDetailsGroupsParamsType = Partial<{
  location_ids: number[]
  search_product: string
  purchase_order_id: number
  is_group: boolean
  'pagination.page_index': number
  'pagination.page_size': number
  'pagination.sort': string
  'pagination.direction': 'asc' | 'desc'
}>

export type InventoryDetailGroupType = {
  inventory_detail_id: string
  location_id: string
  product: {
    id: string
    name: string
    sku_number: string
    image: string
    grammation: string
    business_taggings: { id: string; key: string; type: string; value: string }[]
  }
  total_stock: number
  status: { id: number; name: string }
  status_note: { id: string; name: string }
  qr_code: string
  expiry_date: string
}

export type GetInventoryDetailsGroupsResponseType = {
  data: InventoryDetailGroupType[]
  pagination: { sort: string; direction: string; page_size: number; page_index: number }
  error: { status: boolean; code: number; message: string }
}

export const getInventoryDetailsGroupsAPI = (params: GetInventoryDetailsGroupsParamsType) =>
  axiosInstanceApiGateway.Get<GetInventoryDetailsGroupsResponseType, true>({
    url: '/wims/internal/v1/inventory-details:groups',
    params,
    config: {
      paramsSerializer: {
        serialize: (p) => buildQueryParams(p).toString(),
      },
    },
  })

export type GetSOPickingCompilationResponseType = {
  data: {
    valid: boolean
    message: string
  }
}

export const postSOPickingCompilationAPI = (supplyOrderId: number) =>
  axiosInstanceApiGateway.Post<GetSOPickingCompilationResponseType, true>({
    url: `/wims/internal/v1/supply-orders/${supplyOrderId}/picking-completion`,
  })
